import { useState } from 'react';
import { KittingTagsData } from '../../../../redux/api/order-line-item';

export interface SelectedKittingTags {
	toolIndices: number[];
	jawIndices: number[];
	otherTagKeys: string[];
}

export interface KittingTagGroupSelection<T> {
	selected: Set<T>;
	allSelected: boolean;
	toggle: (value: T) => void;
	toggleAll: () => void;
}

export interface KittingTagSelection {
	tools: KittingTagGroupSelection<number>;
	jaws: KittingTagGroupSelection<number>;
	other: KittingTagGroupSelection<string>;
	special: KittingTagGroupSelection<string>;
	totalSelected: number;
	allSelected: boolean;
	toggleEverything: () => void;
	clear: () => void;
	selectedTags: () => SelectedKittingTags;
}

const useGroup = <T,>(values: T[]): KittingTagGroupSelection<T> & { clear: () => void; selectAll: () => void } => {
	const [selected, setSelected] = useState<Set<T>>(new Set());
	const allSelected = values.length > 0 && selected.size === values.length;

	return {
		selected,
		allSelected,
		toggle: (value) =>
			setSelected((prev) => {
				const next = new Set(prev);
				if (next.has(value)) next.delete(value);
				else next.add(value);
				return next;
			}),
		toggleAll: () => setSelected(allSelected ? new Set() : new Set(values)),
		clear: () => setSelected(new Set()),
		selectAll: () => setSelected(new Set(values)),
	};
};

export const useKittingTagSelection = (data: KittingTagsData | null | undefined): KittingTagSelection => {
	const tools = useGroup(data?.tools.map((t) => t.index) ?? []);
	const jaws = useGroup(data?.jaws.map((j) => j.index) ?? []);
	const other = useGroup(data?.other_tags?.map((t) => t.key) ?? []);
	const special = useGroup(data?.special_tags?.map((t) => t.key) ?? []);
	const groups = [tools, jaws, other, special];

	const totalItems =
		(data?.tools.length ?? 0) +
		(data?.jaws.length ?? 0) +
		(data?.other_tags?.length ?? 0) +
		(data?.special_tags?.length ?? 0);
	const totalSelected = groups.reduce((sum, group) => sum + group.selected.size, 0);
	const allSelected = totalSelected > 0 && totalSelected === totalItems;

	const clear = () => groups.forEach((group) => group.clear());

	return {
		tools,
		jaws,
		other,
		special,
		totalSelected,
		allSelected,
		toggleEverything: () => (allSelected ? clear() : groups.forEach((group) => group.selectAll())),
		clear,
		// The backend groups Forms 68-70 apart for display, but the print endpoint
		// resolves every non-tool, non-jaw tag from one flat key list.
		selectedTags: () => ({
			toolIndices: Array.from(tools.selected),
			jawIndices: Array.from(jaws.selected),
			otherTagKeys: [...other.selected, ...special.selected],
		}),
	};
};
